import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Checkbox } from 'antd';
import { SearchOutlined, ClearOutlined, CheckCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW150KQuery } from './hooks/usePW150KQuery';
import type { VendorQualRow, ApproveItem } from '../../services/pw150kApi';
import './PW150KForm.css';

interface LocalRow extends VendorQualRow {
  checked: boolean;
  note: string;
}

const PW150KForm: React.FC = () => {
  const { loading, approving, data, queried, noData, runQuery, approve, reset } = usePW150KQuery();
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');
  const [localData, setLocalData] = useState<LocalRow[]>([]);

  const handleQuery = () => {
    runQuery({ vendorFrom: vendorFrom.trim() || undefined, vendorTo: vendorTo.trim() || undefined });
  };

  // Sync data from query to local editable state
  React.useEffect(() => {
    setLocalData(data.map(r => ({ ...r, checked: false, note: '' })));
  }, [data]);

  const handleReset = () => { setVendorFrom(''); setVendorTo(''); setLocalData([]); reset(); };

  const handleCheck = (idx: number, checked: boolean) => {
    setLocalData(prev => prev.map((r, i) => i === idx ? { ...r, checked } : r));
  };

  const handleNoteChange = (idx: number, note: string) => {
    setLocalData(prev => prev.map((r, i) => i === idx ? { ...r, note } : r));
  };

  const handleApprove = async () => {
    const items: ApproveItem[] = localData
      .filter(r => r.checked)
      .map(r => ({ doc_no: r.doc_no, gm_note: r.note }));
    const result = await approve(items);
    if (result) {
      handleQuery(); // refresh
    }
  };

  const checkedCount = localData.filter(r => r.checked).length;

  const columns: ColumnsType<LocalRow> = [
    {
      title: '核准', key: 'check', width: 60, align: 'center' as const,
      render: (_: unknown, record: LocalRow, idx: number) => (
        <Checkbox checked={record.checked} onChange={e => handleCheck(idx, e.target.checked)} />
      ),
    },
    { title: '評鑑單號', dataIndex: 'doc_no', key: 'doc_no', width: 140 },
    { title: '類別', dataIndex: 'vendor_type', key: 'vendor_type', width: 60 },
    { title: '部門核准說明', dataIndex: 'dept_approval', key: 'dept_approval', ellipsis: true },
    { title: '研發核准說明', dataIndex: 'rd_approval', key: 'rd_approval', ellipsis: true },
    { title: '總經理核准說明', dataIndex: 'gm_approval', key: 'gm_approval', ellipsis: true },
    {
      title: '核准說明', key: 'gm_note', width: 200,
      render: (_: unknown, record: LocalRow, idx: number) => (
        <Input.TextArea
          value={record.note}
          onChange={e => handleNoteChange(idx, e.target.value)}
          rows={1}
          placeholder="輸入核准說明"
          disabled={!record.checked}
        />
      ),
    },
  ];

  return (
    <PermissionGuard module={Modules.PW150K} action="query">
      <div className="pw150k-container">
        <div className="pw150k-header">
          <h2>廠商資格核准-總經理</h2>
          {queried && !noData && (
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleApprove}
              loading={approving} disabled={checkedCount === 0}>
              核准 ({checkedCount})
            </Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>公司代號起</label>
              <Input value={vendorFrom} onChange={e => setVendorFrom(e.target.value)} placeholder="起始代號" style={{ width: 140 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>公司代號迄</label>
              <Input value={vendorTo} onChange={e => setVendorTo(e.target.value)} placeholder="結束代號" style={{ width: 140 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>確定範圍</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="無須核准資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <Table<LocalRow> columns={columns} dataSource={localData} rowKey="doc_no" loading={loading} size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }} scroll={{ x: 1000 }} />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW150KForm;
